// Step 1 — UGC picker (Phase 2b + Phase 7 batch multi-select).
//
// Media grid filtered to social-media sources (Instagram, Apify IG
// scrape) since those are the sources UGC comes from. socialPostType
// filtering per the spec would need an API param; the source filter
// is a strict subset in practice (every 'instagram' / 'apify-ig'
// Media is UGC by definition) and keeps Phase 2 frontend-only.
//
// PHASE 7 MULTI-SELECT — click-to-toggle cards, capped at
// MAX_UGCS_PER_BATCH. Beyond the cap, further clicks are refused with
// visible feedback rather than silently swapping — the picked set is
// the operator's shopping list, not a most-recent-N. Cap is enforced
// server-side too (per-UGC generate calls) so a bypass would just fail
// at Confirm.

import { useEffect, useMemo, useState } from 'react';
import {
  Box, VStack, HStack, Text, Badge, Image, Grid, GridItem,
  Spinner, Input, Button, useToast
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { MAX_UGCS_PER_BATCH } from './types';

type MediaRow = {
  mediaId:       string;
  source:        string | null;
  externalId:    string | null;
  fileType:      string | null;
  fileUrl:       string | null;
  fileName:      string | null;
  brand:         string | null;
  caption:       string | null;
  postedAt:      string | null;
  creatorHandle: string | null;
  postType:      string | null;
  createdAt:     string | null;
};

type Props = {
  selectedMediaIds: string[];
  onChangeMediaIds: (ids: string[]) => void;
};

// Sources that indicate UGC — matches the Phase 2 spec's OR clause.
// operator-assigned Media are also UGC-adjacent but they come from
// the same sources in practice, so this filter covers both.
const UGC_SOURCES = new Set(['instagram', 'apify-ig']);

export function Step1UgcPicker({ selectedMediaIds, onChangeMediaIds }: Props) {
  const toast = useToast();
  const [rows, setRows]     = useState<MediaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [q, setQ]             = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset]   = useState(0);
  const PAGE_SIZE = 48;

  const selectedSet = useMemo(() => new Set(selectedMediaIds), [selectedMediaIds]);
  const atCap = selectedMediaIds.length >= MAX_UGCS_PER_BATCH;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await apiJson<{ media: MediaRow[]; hasMore: boolean }>(
          `/api/media?limit=${PAGE_SIZE}&offset=${offset}`
        );
        if (cancelled) return;
        setRows(prev => offset === 0 ? res.media : [...prev, ...res.media]);
        setHasMore(!!res.hasMore);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load media');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [offset]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter(r => {
      if (r.source && !UGC_SOURCES.has(r.source)) return false;
      if (!term) return true;
      return (
        (r.caption || '').toLowerCase().includes(term) ||
        (r.creatorHandle || '').toLowerCase().includes(term) ||
        (r.externalId || '').toLowerCase().includes(term)
      );
    });
  }, [rows, q]);

  const toggle = (mediaId: string) => {
    if (selectedSet.has(mediaId)) {
      onChangeMediaIds(selectedMediaIds.filter(id => id !== mediaId));
      return;
    }
    if (atCap) {
      toast({
        title: `Batch cap reached`,
        description: `Max ${MAX_UGCS_PER_BATCH} UGCs per batch. Deselect one to add another.`,
        status: 'warning',
        duration: 3000
      });
      return;
    }
    onChangeMediaIds([...selectedMediaIds, mediaId]);
  };

  const clearAll = () => onChangeMediaIds([]);

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="flex-end">
        <Box>
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={1}>
            Choose UGC posts
          </Text>
          <Text fontSize="xs" color="brand.muted">
            {selectedMediaIds.length === 0
              ? 'Pick one or more posts. Batch generation creates ads per UGC.'
              : selectedMediaIds.length === 1
                ? '1 UGC selected.'
                : `${selectedMediaIds.length} UGCs selected — batch mode. Each seeds its own ad generation.`}
            {selectedMediaIds.length > 0 && ` (${MAX_UGCS_PER_BATCH - selectedMediaIds.length} slots left)`}
          </Text>
        </Box>
        {selectedMediaIds.length > 0 && (
          <Button size="xs" variant="ghost" onClick={clearAll}>Clear selection</Button>
        )}
      </HStack>

      <Input
        size="sm"
        placeholder="Filter by caption, handle, or post id"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        maxW="480px"
        bg="white"
      />

      {error && (
        <Text fontSize="sm" color="red.600">{error}</Text>
      )}

      {loading && rows.length === 0 && (
        <HStack py={6} justify="center"><Spinner size="sm" /><Text fontSize="sm" color="brand.muted">Loading…</Text></HStack>
      )}

      {!loading && filtered.length === 0 && (
        <Box borderWidth="1px" borderStyle="dashed" borderColor="brand.border" borderRadius="lg" p={6}>
          <Text fontSize="sm" color="brand.muted">
            No UGC posts found. Connect Instagram or run an Apify scrape from the Integrations page to ingest social media.
          </Text>
        </Box>
      )}

      {filtered.length > 0 && (
        <Grid templateColumns="repeat(auto-fill, minmax(160px, 1fr))" gap={3}>
          {filtered.map(r => {
            const selected = selectedSet.has(r.mediaId);
            // Dim non-selected cards when at cap so the "why can't I
            // click?" is immediately visible rather than only surfacing
            // via the toast on click.
            const dimmed = atCap && !selected;
            return (
              <GridItem
                key={r.mediaId}
                as="button"
                type="button"
                onClick={() => toggle(r.mediaId)}
                borderWidth={selected ? '2px' : '1px'}
                borderColor={selected ? 'rsViolet.500' : 'brand.border'}
                borderRadius="md"
                overflow="hidden"
                bg="white"
                cursor="pointer"
                textAlign="left"
                opacity={dimmed ? 0.45 : 1}
                _hover={{ borderColor: dimmed ? 'brand.border' : 'rsViolet.400' }}
              >
                <Box position="relative" bg="brand.canvas" aspectRatio={1}>
                  {r.fileType === 'video' && r.fileUrl ? (
                    <video
                      src={r.fileUrl}
                      muted
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : r.fileUrl ? (
                    <Image
                      src={r.fileUrl}
                      alt={r.caption || 'UGC'}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                  ) : null}
                  {selected && (
                    <Badge
                      position="absolute"
                      top={1}
                      right={1}
                      colorScheme="purple"
                      fontSize="9px"
                    >
                      Selected
                    </Badge>
                  )}
                </Box>
                <Box p={2}>
                  <HStack spacing={1} flexWrap="wrap" mb={1}>
                    <Badge fontSize="9px" colorScheme="purple" variant="subtle">
                      {r.source || 'unknown'}
                    </Badge>
                    {r.postType && (
                      <Badge fontSize="9px" colorScheme="gray" variant="subtle">
                        {r.postType}
                      </Badge>
                    )}
                  </HStack>
                  {r.creatorHandle && (
                    <Text fontSize="11px" color="brand.ink" fontWeight="700" noOfLines={1}>
                      @{r.creatorHandle}
                    </Text>
                  )}
                  {r.caption && (
                    <Text fontSize="10px" color="brand.muted" noOfLines={2} mt={0.5}>
                      {r.caption}
                    </Text>
                  )}
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      )}

      {hasMore && !loading && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOffset(offset + PAGE_SIZE)}
          alignSelf="flex-start"
        >
          Load more
        </Button>
      )}
    </VStack>
  );
}
